import * as yup from 'yup';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { GetScannerQuery as GetScanner } from '__generated__/graphql';
import { encodeUrl, getServerErrors } from '../../../common/helpers';
import UI from '../../../common/UI';
import useEditScannerMutation from './useEditScannerMutation';
import useNavigationPrompt from '../../../common/useNavigationPrompt';
import useProject from '../../useProject';
import useValidation from '../../../common/useValidation';

type Scanner = GetScanner['project']['scanner'];

const editScannerInfoSchema = yup.object().shape({
  title: yup.string().ensure().required(),
});

export interface EditScannerInfoFormProps {
  scanner: Scanner;
}

const EditScannerInfoForm = ({ scanner }: EditScannerInfoFormProps) => {
  const { t } = useTranslation();
  const project = useProject();

  const getValues = (scanner: Scanner) => ({
    title: scanner.title,
    edit_start_numbers: scanner.edit_start_numbers,
  });

  const form = useForm({
    values: getValues(scanner),
  });

  useNavigationPrompt(form.changed());

  const [editScanner, { error, loading }] = useEditScannerMutation({
    input: {
      id: scanner.id,
      info: form.values,
    },
    onSuccess: (scanner) => {
      form.reset(getValues(scanner));
    },
  });

  const { valid, errors } = useValidation({
    schema: editScannerInfoSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => editScanner()}>
      <UI.FormGrid>
        <UI.ServerErrors error={error} />
        <UI.Div>
          <UI.Legend sc={{ mb: 1 }}>{t('scanner')}</UI.Legend>
          <UI.InputDescription>
            {t('scanner_description')}
          </UI.InputDescription>
        </UI.Div>
        <UI.InputGroup sc={{ valid: !errors.title, touched: form.touched.title }}>
          <UI.InputLabel htmlFor="ScannerTitle">
            {t('title')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <UI.DebouncedInput
            {...form.text('title')}
            maxLength={255}
            placeholder={t('scanner_title_placeholder')}
            autoFocus={!scanner}
            id="ScannerTitle"
          />
          <UI.ErrorMessages attribute={t('title')} errors={errors.title} />
        </UI.InputGroup>
        <UI.InputGroup>
          <UI.InputLabel>{t('edit_start_numbers')}</UI.InputLabel>
          <UI.InputDescription>
            {t('edit_start_numbers_description')}
          </UI.InputDescription>
          <UI.Checkbox
            onChange={() => form.set((values) => ({ ...values, edit_start_numbers: !values.edit_start_numbers }))}
            checked={form.values.edit_start_numbers}
          >
            {t('allow_editing_start_numbers')}
          </UI.Checkbox>
        </UI.InputGroup>

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('save_scanner')}
          closeTo={`/${project.id}/participants/scanners?${encodeUrl({ event: scanner.event.id })}`}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default EditScannerInfoForm;
